/*
  <aoa-rail>: horizontal rail over a native scroll-snap track (markup rendered by Liquid).
  Without JS the track is a plain swipeable scroller; this component only adds:
  - Arrows (768px and up) with aria-disabled at the ends, scroll by page or by item, keyboard.
  - Optional infinite loop (attribute "loop", 768px and up, only when the items overflow). It
    uses the copies already rendered by the server as direct children of the track with
    data-aoa-clone, aria-hidden="true", inert and tabindex="-1". Nothing is cloned here.
  - Optional progress bar (attribute "progress").
  - Optional pages (attribute "pages", 2026-10-02): from 768px, without loop, the rail rests on whole
    pages and the bar is one piece whose thumb is one page wide and jumps page by page.
    pages="item" (2026-10-22, Bryan: «uno por uno y que se guíe de la imagen del medio»): same, but the
    step is one card and the thumb is one card wide, under the card in the middle of the view.
  - Optional outside arrows for phones: buttons [data-aoa-rail-go="-1|1"] inside a [data-aoa-rail-ext]
    group anywhere in the closest [data-aoa-rail-scope] (the section title row). With several rails in
    one scope (tabs), the visible one answers and keeps their aria-disabled; the group is shown by JS.
  - The track is a focusable region (tabindex 0) only while it overflows: a row that fits
    is no Tab stop.
  - Glide (2026-10-29, Bryan: «está muy brusco el movimiento»): mouse drag, arrows, keys and the
    paged wheel move the track with one eased animation, snap off until it rests on a card. Before,
    letting go of a drag re-enabled the mandatory snap and the browser jumped to the nearest card in
    a single frame. Touch keeps the native swipe; an animation stops as soon as a finger lands.
  Inside <aoa-tabs> the rail is measured once the tabs have hidden the inactive panels, in any
  script order (customElements.whenDefined), or once the page has loaded if aoa-tabs.js never runs.
  Listeners are passive where possible and bound to the element itself (bubble phase); no
  MutationObserver, nothing on window. Clean up in disconnectedCallback.
*/
if (!customElements.get('aoa-rail')) {
  class AoaRail extends HTMLElement {
    constructor() {
      super();
      this.looping = false;
      this.holdUntil = 0;
      this.frame = 0;
      this.endTimer = 0;
      this.glide = 0;
      this.glideEnd = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-aoa-rail-track]');
      if (!this.track || this.controller) return;
      const controller = new AbortController();
      this.controller = controller;

      // Measured before the tabs hide their inactive panels, a hidden rail would switch its loop on
      // and load the tail photos eagerly: wait for <aoa-tabs> whatever the script order
      if (this.closest('aoa-tabs') && !customElements.get('aoa-tabs')) {
        Promise.race([customElements.whenDefined('aoa-tabs'), this.pageLoaded()]).then(() => {
          if (this.controller === controller) this.start();
        });
      } else {
        this.start();
      }
    }

    // Fallback when aoa-tabs.js fails: every panel stays visible, so the rail can start
    pageLoaded() {
      return new Promise((resolve) => {
        if (document.readyState === 'complete') {
          resolve();
          return;
        }
        document.addEventListener('readystatechange', () => {
          if (document.readyState === 'complete') resolve();
        }, { signal: this.controller.signal });
      });
    }

    start() {
      this.prevButton = this.querySelector('[data-aoa-rail-prev]');
      this.nextButton = this.querySelector('[data-aoa-rail-next]');
      this.progress = this.hasAttribute('progress') ? this.querySelector('[data-aoa-rail-progress]') : null;
      const scope = this.closest('[data-aoa-rail-scope]');
      this.external = scope ? Array.from(scope.querySelectorAll('[data-aoa-rail-ext] [data-aoa-rail-go]')) : [];
      this.wide = window.matchMedia('(min-width: 768px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const options = { signal: this.controller.signal };
      const passive = { passive: true, signal: this.controller.signal };

      this.track.addEventListener('scroll', this.onScroll, passive);
      if ('onscrollend' in window) this.track.addEventListener('scrollend', this.onScrollEnd, passive);
      this.track.addEventListener('pointerdown', this.release, passive);
      // Not passive: at the first item, going back, the wheel step is taken over (see onWheel)
      this.track.addEventListener('wheel', this.onWheel, { passive: false, signal: this.controller.signal });
      if (this.hasAttribute('drag')) {
        this.track.addEventListener('pointerdown', this.onDragStart, options);
        this.track.addEventListener('pointermove', this.onDragMove, options);
        this.track.addEventListener('pointerup', this.onDragEnd, options);
        this.track.addEventListener('pointercancel', this.onDragEnd, options);
        // Native drag of links and photos would steal the gesture
        this.track.addEventListener('dragstart', (event) => event.preventDefault(), options);
      }
      this.track.addEventListener('keydown', this.onKeydown, options);
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('aoa:card-change', this.onItemChange, options);
      this.wide.addEventListener('change', this.update, options);

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      [this.prevButton, this.nextButton].forEach((button) => {
        if (button) button.hidden = false;
      });
      this.external.forEach((button) => {
        button.addEventListener('click', this.onExternalClick, options);
        button.closest('[data-aoa-rail-ext]').hidden = false;
      });
      this.update();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      cancelAnimationFrame(this.frame);
      this.stopGlide();
      clearTimeout(this.endTimer);
      clearTimeout(this.wheelTimer);
      this.controller = null;
      this.resizeObserver = null;
    }

    /* ---------- items ---------- */

    children(clones) {
      return Array.from(this.track.children).filter((item) => item.hasAttribute('data-aoa-clone') === clones);
    }

    originals() {
      return this.children(false);
    }

    clones() {
      return this.children(true);
    }

    visibleOriginals() {
      return this.originals().filter((item) => item.offsetParent !== null);
    }

    itemStep() {
      const items = this.visibleOriginals();
      if (!items.length) return 0;
      return items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : items[0].offsetWidth;
    }

    pageStep(itemStep) {
      if (this.getAttribute('pages') === 'item') return itemStep;
      const gap = itemStep - this.visibleOriginals()[0].offsetWidth;
      return Math.max(1, Math.floor((this.track.clientWidth + gap + 1) / itemStep)) * itemStep;
    }

    period() {
      const first = this.visibleOriginals()[0];
      const firstClone = this.clones()[0];
      return first && firstClone && firstClone.offsetParent !== null ? firstClone.offsetLeft - first.offsetLeft : 0;
    }

    /* ---------- state ---------- */

    update = () => {
      // Not started yet (waiting for <aoa-tabs>): nothing measured, nothing to update
      if (!this.track || !this.wide) return;
      const items = this.visibleOriginals();
      const last = items[items.length - 1];
      const overflows = items.length > 1 && last.offsetLeft + last.offsetWidth - items[0].offsetLeft > this.track.clientWidth + 1;
      const canLoop = this.hasAttribute('loop') && this.wide.matches && overflows && this.clones().length === this.originals().length;

      if (canLoop !== this.looping) {
        if (!canLoop) this.normalize();
        this.looping = canLoop;
        this.setAttribute('data-loop', canLoop ? 'on' : 'off');
        if (canLoop) this.loadTail(items);
      }
      this.toggleAttribute('data-overflow', overflows);
      // Pages (attribute "pages", 2026-10-02): from 768px and without loop, the rail moves one page
      // (the visible items) at a time and the progress bar jumps page by page. Phones keep the
      // continuous bar and the native swipe.
      this.paging = this.hasAttribute('pages') && this.wide.matches && !this.looping && overflows;
      this.setAttribute('data-pages', this.paging ? 'on' : 'off');
      this.setFocusable(overflows);
      this.sync();
    };

    /* ---------- pages ---------- */

    pageCount(pageStep) {
      return Math.max(1, Math.ceil((this.track.scrollWidth - 1) / Math.max(1, pageStep)));
    }

    currentPage(pageStep) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 2) return 0;
      const pages = this.pageCount(pageStep);
      // The last page rests at the end of the track, not at a multiple of the page step
      if (this.track.scrollLeft >= max - 2) return pages - 1;
      return Math.min(pages - 1, Math.round(this.track.scrollLeft / pageStep));
    }

    // Rest on a whole page: after a drag or a wheel/trackpad scroll that stopped between pages
    snapToPage() {
      if (!this.paging || this.dragState || this.glide) return;
      const itemStep = this.itemStep();
      if (!itemStep) return;
      const pageStep = this.pageStep(itemStep);
      const max = this.track.scrollWidth - this.track.clientWidth;
      const target = Math.min(max, this.currentPage(pageStep) * pageStep);
      if (Math.abs(target - this.track.scrollLeft) > 2) this.glideTo(target);
    }

    // Scrollable region: in the Tab order only while there is something to scroll
    setFocusable(overflows) {
      const track = this.track;
      if (overflows) {
        if (track.getAttribute('tabindex') !== '0') track.setAttribute('tabindex', '0');
      } else if (track.hasAttribute('tabindex')) {
        // Never pull focus from under the user: drop the stop when the track loses focus
        if (track === document.activeElement) {
          track.addEventListener('blur', this.update, { once: true, signal: this.controller.signal });
        } else {
          track.removeAttribute('tabindex');
        }
      }
    }

    sync() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const noScroll = max <= 2;
      const atStart = !this.looping && (noScroll || track.scrollLeft <= 2);
      const atEnd = !this.looping && (noScroll || track.scrollLeft >= max - 2);

      // A hidden arrow cannot keep focus: hand it to the other one
      if (atStart && !atEnd && this.prevButton === document.activeElement && this.nextButton) this.nextButton.focus({ preventScroll: true });
      if (atEnd && !atStart && this.nextButton === document.activeElement && this.prevButton) this.prevButton.focus({ preventScroll: true });
      this.setDisabled(this.prevButton, atStart);
      this.setDisabled(this.nextButton, atEnd);
      // Outside arrows follow the rail on screen (the active tab); a hidden rail leaves them alone
      if (this.external.length && this.offsetParent !== null) {
        this.external.forEach((button) => this.setDisabled(button, button.dataset.aoaRailGo === '-1' ? atStart : atEnd));
        this.external[0].closest('[data-aoa-rail-ext]').toggleAttribute('data-aoa-rail-fits', noScroll);
      }

      if (this.progress && this.paging && this.getAttribute('pages') === 'item') {
        // Card by card (2026-10-22): the thumb is one card wide and sits under the card in the middle of
        // the view (first visible + half of the visible ones). It follows the scroll frame by frame, so it
        // glides with the cards instead of jumping when they stop (Bryan: «lo siento brusco»)
        const items = this.visibleOriginals();
        const count = items.length;
        const itemStep = Math.max(1, this.itemStep());
        const visible = Math.max(1, Math.round((track.clientWidth + itemStep - items[0].offsetWidth) / itemStep));
        const max = track.scrollWidth - track.clientWidth;
        const last = Math.max(0, count - visible);
        const first = max > 2 ? (track.scrollLeft / max) * last : 0;
        const half = Math.floor(visible / 2);
        const middle = Math.min(count - 1, Math.max(0, first + half));
        // At the start the bar also covers the cards before the middle one, at the end the cards after it
        // (Bryan: «al principio o al final se llene la línea de los extremos»); in between it is one card wide,
        // and it grows or shrinks along the first and the last step
        const start = middle - half * Math.max(0, 1 - first);
        const end = middle + 1 + half * Math.max(0, 1 - (last - first));
        const size = Math.min(1, (end - start) / count);
        this.progress.style.setProperty('--aoa-rail-progress-size', size.toFixed(4));
        this.progress.style.setProperty('--aoa-rail-progress-pos', size < 1 ? (start / count / (1 - size)).toFixed(4) : '0');
      } else if (this.progress && this.paging) {
        // One whole bar, no cuts: the thumb is one page wide and jumps from page to page (2026-10-02)
        const pageStep = this.pageStep(Math.max(1, this.itemStep()));
        const pages = this.pageCount(pageStep);
        this.progress.style.setProperty('--aoa-rail-progress-size', (1 / pages).toFixed(4));
        this.progress.style.setProperty('--aoa-rail-progress-pos', pages > 1 ? (this.currentPage(pageStep) / (pages - 1)).toFixed(4) : '0');
      } else if (this.progress) {
        const range = this.looping ? this.period() : track.scrollWidth;
        const size = Math.min(1, track.clientWidth / Math.max(1, range));
        const position = this.looping ? track.scrollLeft % Math.max(1, range) : track.scrollLeft;
        const travel = Math.max(1, range - track.clientWidth);
        this.progress.style.setProperty('--aoa-rail-progress-size', size.toFixed(4));
        this.progress.style.setProperty('--aoa-rail-progress-pos', Math.min(1, position / travel).toFixed(4));
      }
    }

    setDisabled(button, disabled) {
      if (!button) return;
      const value = String(disabled);
      if (button.getAttribute('aria-disabled') !== value) button.setAttribute('aria-disabled', value);
    }

    // Images of the last items are shown at once when going back from the first one
    loadTail(items) {
      const visible = Math.max(1, Math.round(this.track.clientWidth / Math.max(1, this.itemStep())));
      items.slice(-visible).forEach((item) => {
        item.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
      });
    }

    /* ---------- movement ---------- */

    jump(left) {
      this.track.scrollLeft = left;
    }

    // Keep the resting position inside the originals: a copy is identical, the jump is invisible
    normalize() {
      const period = this.period();
      if (period > 0 && this.track.scrollLeft >= period - 1) this.jump(this.track.scrollLeft - period);
    }

    go(direction) {
      const itemStep = this.itemStep();
      if (!itemStep) return;
      const byItem = !this.paging && (this.getAttribute('step') || (this.looping ? 'item' : 'page')) === 'item';
      const distance = byItem ? itemStep : this.pageStep(itemStep);
      // A click while the track is still gliding counts from where it is going, not from where it is
      let from = this.glide ? this.glideEnd : this.track.scrollLeft;
      this.stopGlide();

      if (this.looping) {
        const period = this.period();
        if (period > 0 && direction > 0) {
          this.holdUntil = 0;
          if (from >= period - 1) {
            from -= period;
            this.jump(this.track.scrollLeft - period);
          }
        }
        if (period > 0 && direction < 0 && from < distance - 1) {
          // Going back from the start: move to the same place among the copies first
          this.holdUntil = performance.now() + 1000;
          from += period;
          this.jump(this.track.scrollLeft + period);
        }
      }
      const max = this.track.scrollWidth - this.track.clientWidth;
      // Rest on a card (or on the end of the track): the snap is off while gliding
      let target = Math.round((from + direction * distance) / itemStep) * itemStep;
      if (direction > 0 && target > max - itemStep / 2) target = max;
      this.glideTo(target, 'inOut');
    }

    // Where a drag comes to rest: the card under the projected position, at least the next one
    // when the drag was a flick, never further than one page from where the drag let go
    restingPoint(left, velocity) {
      const itemStep = this.itemStep();
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (!itemStep) return Math.max(0, Math.min(max, left));
      const step = this.paging ? this.pageStep(itemStep) : itemStep;
      let index = Math.round((left + velocity * 220) / step);
      if (velocity > 0.25) index = Math.max(index, Math.ceil(left / step - 0.02));
      if (velocity < -0.25) index = Math.min(index, Math.floor(left / step + 0.02));
      index = Math.max(Math.floor(left / step) - 1, Math.min(Math.ceil(left / step) + 1, index));
      let target = index * step;
      if (target > max - itemStep / 2 && velocity >= 0) target = max;
      return Math.max(0, Math.min(max, target));
    }

    /* ---------- glide ---------- */

    // One eased move with the snap off (inline, so it beats every track's own snap rule, the
    // quick view's #id rule included); the snap comes back once the track rests on a card
    glideTo(left, ease = 'out') {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const to = Math.max(0, Math.min(max, Math.round(left)));
      const from = track.scrollLeft;
      const distance = to - from;
      this.stopGlide();
      if (Math.abs(distance) < 1 || this.reducedMotion.matches) {
        track.scrollLeft = to;
        this.endGlide();
        return;
      }
      // Longer moves take a little longer, never sluggish: 320 to 620 ms
      const duration = Math.min(620, 320 + Math.abs(distance) * 0.3);
      const curve = ease === 'inOut'
        ? (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
        : (t) => 1 - Math.pow(1 - t, 3);
      const start = performance.now();
      this.glideEnd = to;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        track.scrollLeft = from + distance * curve(t);
        if (t < 1) {
          this.glide = requestAnimationFrame(step);
        } else {
          this.glide = 0;
          this.endGlide();
        }
      };
      this.glide = requestAnimationFrame(step);
    }

    endGlide() {
      this.restoreSnap();
      this.wrap();
      this.sync();
    }

    restoreSnap() {
      this.track.style.scrollSnapType = '';
      this.track.style.scrollBehavior = '';
    }

    // A finger, the mouse or the wheel takes over: stop where the track is
    stopGlide = (keepSnapOff = false) => {
      if (!this.glide) return;
      cancelAnimationFrame(this.glide);
      this.glide = 0;
      if (keepSnapOff !== true) this.restoreSnap();
    };

    wrap() {
      if (!this.looping || performance.now() < this.holdUntil) return;
      this.normalize();
    }

    /* ---------- events ---------- */

    onScroll = () => {
      if (!this.frame) {
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync();
        });
      }
      // scrollend is Baseline newly available: debounce where it is missing
      if (!('onscrollend' in window)) {
        clearTimeout(this.endTimer);
        this.endTimer = setTimeout(this.onScrollEnd, 150);
      }
    };

    onScrollEnd = () => {
      // Mid-glide the track only pauses between frames: the glide wraps and syncs when it ends
      if (this.glide) return;
      this.wrap();
      this.sync();
      this.snapToPage();
    };

    release = () => {
      this.holdUntil = 0;
      if (!this.glide) return;
      // Stop where the track is, snap still off: switching the mandatory snap back on mid-way would
      // jump to a card before the finger moves. It comes back when the finger or the button lifts
      // (a touch swipe sends pointercancel as it starts), unless a drag has started a new glide.
      this.stopGlide(true);
      const restore = () => {
        if (!this.glide) this.restoreSnap();
      };
      const once = { once: true, signal: this.controller.signal };
      this.track.addEventListener('pointerup', restore, once);
      this.track.addEventListener('pointercancel', restore, once);
    };

    /* Optional mouse drag (attribute "drag", added 2026-09-30 for the PDP rails, as in the mockups):
       past 4 px the snap turns off and the track follows the mouse; on release it snaps again and
       the click that ends a drag does not open the card. While looping it wraps both ways. Touch
       keeps the native swipe. The wheel/trackpad also wraps backwards from the first item while
       looping (onWheel below). */
    // Backwards from the first item (wheel or trackpad): continue among the copies. The browser
    // scrolls the wheel off the main thread and would ignore a jump made here, so only this one
    // step at the edge is taken over; the rest of the gesture scrolls natively. Vertical wheel
    // (page scroll over the rail) is never touched.
    onWheel = (event) => {
      this.holdUntil = 0;
      // Pages (2026-10-02, Bryan): the wheel over the rail turns one page per notch, horizontal or
      // vertical. At the first page going back and at the last page going on, the wheel is left
      // alone so the page keeps scrolling: the rail never traps the scroll.
      if (this.paging) {
        // 2026-10-12 (Bryan: «a veces no se puede bajar ni subir»): la rueda VERTICAL ya no se captura nunca; la
        // página sigue bajando aunque el cursor esté sobre el riel. El riel pasa de página solo con giro
        // horizontal (trackpad) o Shift + rueda, además de las flechas y el arrastre. Antes, con el riel en la
        // última página y una tolerancia de 2 px que no se cumplía (quedaba en 1323 de 1333), la rueda se quedaba
        // atrapada: bajar no hacía nada y subir movía tarjetas. Paka y norda tampoco capturan la rueda vertical;
        // Baymard lo lista como «scroll hijacking».
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
        if (!delta) return;
        const direction = delta > 0 ? 1 : -1;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const atStart = this.track.scrollLeft <= 8;
        const atEnd = this.track.scrollLeft >= max - 8;
        if ((direction < 0 && atStart) || (direction > 0 && atEnd)) return;
        event.preventDefault();
        // One gesture, one card (2026-10-29): a trackpad keeps sending wheel events for about a second
        // after the fingers leave, so the lock lasts until the events stop for 220 ms, not a fixed
        // 500 ms that let the tail of the same swipe move a second card mid-glide
        clearTimeout(this.wheelTimer);
        this.wheelTimer = setTimeout(() => { this.wheelLock = false; }, 220);
        if (this.wheelLock) return;
        this.wheelLock = true;
        this.go(direction);
        return;
      }
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      // A sideways wheel takes over from a glide instead of fighting it; a page scroll leaves it alone
      if (horizontal) this.stopGlide();
      if (!this.looping) return;
      const period = this.period();
      if (horizontal < 0 && period > 0 && this.track.scrollLeft < 2) {
        event.preventDefault();
        this.jump(this.track.scrollLeft + period);
        this.track.scrollBy({ left: horizontal, behavior: 'auto' });
      }
    };

    /* ---------- mouse drag (attribute "drag") ---------- */

    onDragStart = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !this.hasAttribute('data-overflow')) return;
      this.dragState = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, moved: false, samples: [] };
    };

    onDragMove = (event) => {
      const drag = this.dragState;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 4) return;
        drag.moved = true;
        this.track.setPointerCapture(event.pointerId);
        this.setAttribute('data-dragging', '');
      }
      let left = drag.left - dx;
      const period = this.looping ? this.period() : 0;
      // Endless both ways: a copy is identical, the shift is invisible
      if (period > 0 && left < 0) {
        drag.left += period;
        left += period;
      } else if (period > 0 && left >= period) {
        drag.left -= period;
        left -= period;
      }
      this.track.scrollLeft = left;
      // The last moves give the speed of the release (the flick)
      drag.samples.push({ t: performance.now(), x: event.clientX });
      if (drag.samples.length > 6) drag.samples.shift();
    };

    // Track speed at the release, in px/ms along the scroll (moving the mouse left scrolls forward).
    // A pause before letting go means no flick.
    releaseVelocity(drag) {
      const samples = drag.samples;
      const now = performance.now();
      const recent = samples.filter((sample) => now - sample.t < 100);
      if (recent.length < 2) return 0;
      const first = recent[0];
      const last = recent[recent.length - 1];
      const time = Math.max(1, last.t - first.t);
      return -(last.x - first.x) / time;
    }

    onDragEnd = (event) => {
      const drag = this.dragState;
      if (!drag || event.pointerId !== drag.id) return;
      this.dragState = null;
      if (!drag.moved) return;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      // Glide to the resting card before the snap comes back: the inline snap-off set by glideTo
      // holds the track where the mouse left it, so dropping data-dragging no longer jumps
      this.glideTo(this.restingPoint(this.track.scrollLeft, this.releaseVelocity(drag)));
      this.removeAttribute('data-dragging');
      // The click that ends a drag must not open the card under the pointer. It fires right after
      // pointerup, so the block only lives for this task: a release outside the rail sends no click
      // and must not swallow the next real one.
      const block = (click) => {
        click.preventDefault();
        click.stopPropagation();
      };
      this.addEventListener('click', block, true);
      setTimeout(() => {
        this.removeEventListener('click', block, true);
        this.sync();
      }, 0);
    };

    onKeydown = (event) => {
      if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(event.key === 'ArrowRight' ? 1 : -1);
      } else if (!this.looping && (event.key === 'Home' || event.key === 'End')) {
        event.preventDefault();
        this.glideTo(event.key === 'Home' ? 0 : this.track.scrollWidth, 'inOut');
      }
    };

    onExternalClick = (event) => {
      if (this.offsetParent === null) return;
      const button = event.currentTarget;
      if (button.getAttribute('aria-disabled') !== 'true') this.go(Number(button.dataset.aoaRailGo));
    };

    onClick = (event) => {
      const arrow = event.target.closest('[data-aoa-rail-prev], [data-aoa-rail-next]');
      if (arrow && arrow.closest('aoa-rail') === this) {
        if (arrow.getAttribute('aria-disabled') !== 'true') this.go(arrow.hasAttribute('data-aoa-rail-next') ? 1 : -1);
        return;
      }
      if (this.looping && event.target === this.track) this.forwardFromClone(event);
    };

    // Copies are inert, so a click on them lands on the track: repeat it on the original
    forwardFromClone(event) {
      const inside = (rect) => event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      const clones = this.clones();
      const index = clones.findIndex((clone) => inside(clone.getBoundingClientRect()));
      const original = this.originals()[index];
      if (index < 0 || !original) return;

      // The item itself can be the control (a card that is one link): include it
      const selector = 'a[href], button';
      const controlsOf = (item) => [item, ...item.querySelectorAll(selector)].filter((element) => element.matches(selector));
      const controls = controlsOf(clones[index]);
      const hit = controls.filter((control) => inside(control.getBoundingClientRect())).pop();
      // Same as on the original: a click outside every control (price, weight line) does nothing
      if (!hit) return;
      const target = controlsOf(original)[controls.indexOf(hit)];
      if (!target) return;
      // Same modifiers as the real click (Ctrl/Cmd/Shift + click on a copy opens the link the same way)
      target.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: event.button,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
      }));
    }

    // An original changed (color, "+N"): mirror the state on its copy
    onItemChange = (event) => {
      const source = event.target;
      const item = this.originals().find((original) => original === source || original.contains(source));
      if (!item) return;
      const copy = this.clones()[this.originals().indexOf(item)];
      if (!copy) return;
      const tag = source.localName;
      const sources = [item, ...item.querySelectorAll(tag)].filter((element) => element.localName === tag);
      const copies = [copy, ...copy.querySelectorAll(tag)].filter((element) => element.localName === tag);
      const twin = copies[sources.indexOf(source)];
      if (twin && typeof twin.applyState === 'function') twin.applyState(event.detail);
    };

    // Theme editor: bring a selected block into view
    reveal(element) {
      const item = this.originals().find((original) => original === element || original.contains(element));
      if (item) this.track.scrollTo({ left: item.offsetLeft - this.visibleOriginals()[0].offsetLeft, behavior: 'auto' });
    }
  }

  customElements.define('aoa-rail', AoaRail);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('aoa-rail').forEach((rail) => rail.update());
    });
    document.addEventListener('shopify:block:select', (event) => {
      const rail = event.target.closest('aoa-rail');
      if (rail) rail.reveal(event.target);
    });
  }
}
